import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useOrganizationList, useOrganization, useSession } from '@clerk/clerk-react';
import { apiBaseUrl, publicAnonKey } from '../../../utils/supabase/info';
import { logger } from '../utils/logger';

interface Organization {
  id: string;
  name: string;
  // Real plan names as stored in the database (e.g. 'pilot', 'free', 'starter',
  // 'growth', 'enterprise') — not a fixed enum, since admin can set this to
  // whatever the organizations.plan column allows. Use `formatPlanName()` for display.
  plan: string;
  seats: number;
  usedSeats: number;
}

// organizations.plan is a free-form lowercase string (e.g. 'pilot', 'enterprise');
// this only affects display casing, it doesn't map to a fixed set of tiers.
export const formatPlanName = (plan: string | undefined | null): string => {
  if (!plan) return 'Pilot';
  return plan.charAt(0).toUpperCase() + plan.slice(1);
};

interface Member {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'member';
  avatar?: string;
  joinedAt: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: 'admin' | 'member';
  invitedAt: string;
}

interface Usage {
  runsUsed: number;
  runsLimit: number;
  prospectsUsed: number;
  prospectsLimit: number;
  resetDate: string;
}

// Clerk's default org roles are 'org:admin' / 'org:member' string literals,
// distinct from the app's internal 'admin' | 'member' Member.role type.
const toClerkRole = (role: 'admin' | 'member'): string => (role === 'admin' ? 'org:admin' : 'org:member');
const fromClerkRole = (role: string): 'admin' | 'member' => (role === 'org:admin' ? 'admin' : 'member');

/**
 * Organization context value type
 * @property organizations - List of organizations the user belongs to
 * @property currentOrg - Currently active organization
 * @property switchOrganization - Function to switch to a different org
 * @property members - Members of the current organization (live from Clerk)
 * @property usage - Current usage statistics and limits for the organization
 * @property inviteMember - Function to invite a new member to the organization (sends a real Clerk invitation email)
 * @property removeMember - Function to remove a member from the organization
 * @property updateMemberRole - Function to update a member's role (Admin or Member)
 * @property pendingInvitations - Invitations sent but not yet accepted
 * @property revokeInvitation - Function to revoke a pending invitation
 * @property loadingMembers - Whether members are currently being loaded
 * @property loadingOrg - Whether organization data is currently being loaded
 * @property isCurrentUserAdmin - Function to check if current user is admin
 * @property currentUserMember - The current user's member object in the organization
 * @property createOrganization - Creates a new organization and makes it active
 * @property updateOrganizationName - Renames the current organization
 * @property refreshMembers - Forces a refetch of members and invitations from Clerk
 */
interface OrganizationContextType {
  organizations: Organization[];
  currentOrg: Organization | null;
  switchOrganization: (orgId: string) => void;
  members: Member[];
  usage: Usage;
  inviteMember: (email: string, role: 'admin' | 'member') => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  updateMemberRole: (memberId: string, role: 'admin' | 'member') => Promise<void>;
  pendingInvitations: PendingInvitation[];
  revokeInvitation: (invitationId: string) => Promise<void>;
  loadingMembers: boolean;
  loadingOrg: boolean;
  isCurrentUserAdmin: () => boolean;
  currentUserMember: Member | null;
  createOrganization: (name: string) => Promise<void>;
  updateOrganizationName: (name: string) => Promise<void>;
  refreshMembers: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

/**
 * Internal Organization Provider Implementation
 * This is the actual provider that uses Clerk hooks
 * Should only be rendered when user is authenticated
 */
function OrganizationProviderImpl({ children }: { children: ReactNode }) {
  const { user, getToken } = useAuth();
  const { session } = useSession();

  // SAFETY: This component should only be rendered when user exists
  // The outer OrganizationProvider handles the guard
  const { userMemberships, setActive, isLoaded, createOrganization: clerkCreateOrganization } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });

  // Real membership + invitation data for the active organization, straight from Clerk.
  // This replaces the old Supabase `members` table read, which was always empty because
  // nothing ever synced Clerk membership into it.
  const {
    organization: clerkOrganization,
    membership: currentMembership,
    memberships,
    invitations,
  } = useOrganization({
    memberships: {
      infinite: true,
      keepPreviousData: true,
    },
    invitations: {
      infinite: true,
      keepPreviousData: true,
      status: ['pending'],
    },
  });

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loadingOrg, setLoadingOrg] = useState(true);

  const [usage, setUsage] = useState<Usage>({
    runsUsed: 0,
    runsLimit: 100,
    prospectsUsed: 0,
    prospectsLimit: 1000,
    resetDate: '2025-01-01'
  });

  const members: Member[] = useMemo(() => {
    return (memberships?.data ?? []).map((m) => {
      const first = m.publicUserData?.firstName ?? '';
      const last = m.publicUserData?.lastName ?? '';
      const fullName = `${first} ${last}`.trim();
      return {
        id: m.publicUserData?.userId || m.id,
        name: fullName || m.publicUserData?.identifier || 'Team member',
        email: m.publicUserData?.identifier || '',
        role: fromClerkRole(m.role),
        avatar: m.publicUserData?.imageUrl,
        joinedAt: new Date(m.createdAt).toISOString(),
      };
    });
  }, [memberships?.data]);

  const pendingInvitations: PendingInvitation[] = useMemo(() => {
    return (invitations?.data ?? [])
      .filter((inv) => inv.status === 'pending')
      .map((inv) => ({
        id: inv.id,
        email: inv.emailAddress,
        role: fromClerkRole(inv.role),
        invitedAt: new Date(inv.createdAt).toISOString(),
      }));
  }, [invitations?.data]);

  const loadingMembers = !memberships || memberships.isLoading;

  // Sync Clerk organizations to local state
  useEffect(() => {
    // Guard: Don't process if user not authenticated
    if (!user) {
      setLoadingOrg(false);
      return;
    }

    // Wait for Clerk to load
    if (!isLoaded) {
      return;
    }

    if (userMemberships?.data) {
      const orgs = userMemberships.data.map((membership) => ({
        id: membership.organization.id,
        name: membership.organization.name,
        plan: 'Pilot' as const, // Default to trial/Pilot plan - will be fetched from backend
        seats: 3, // Default minimum seats
        usedSeats: membership.organization.membersCount || 0
      }));
      setOrganizations(orgs);
      logger.success(`Loaded ${orgs.length} Clerk organizations`);

      // Handle no organizations case - set loadingOrg to false
      if (orgs.length === 0) {
        setLoadingOrg(false);
        setCurrentOrg(null);
        logger.info('No organizations found - user needs to create or join an organization');
      }
    } else if (isLoaded) {
      // Clerk is loaded but no memberships data - user has no organizations
      setOrganizations([]);
      setLoadingOrg(false);
      setCurrentOrg(null);
      logger.info('No organizations found - user needs to create or join an organization');
    }
  }, [userMemberships?.data, isLoaded, user]);

  // Set current org from Clerk active organization
  // CRITICAL: Must call setActive() to ensure JWT contains org_id claim
  useEffect(() => {
    if (organizations.length > 0 && setActive && session) {
      const storedOrgId = localStorage.getItem('currentOrgId');
      const matchingOrg = storedOrgId ? organizations.find(o => o.id === storedOrgId) : null;
      const orgToActivate = matchingOrg || organizations[0];
      const shouldStoreOrgId = !matchingOrg;

      // Check if the org is already active
      const activeOrgId = session.lastActiveOrganizationId;
      if (activeOrgId === orgToActivate.id) {
        // Organization is already active, verify token has org_id
        getToken().then(token => {
          if (token) {
            // Decode token to check for org_id claim
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                if (payload.org_id === orgToActivate.id) {
                  logger.success(`Organization already active with valid token: ${orgToActivate.name}`);
                  setCurrentOrg(orgToActivate);
                  setLoadingOrg(false);
                  return;
                }
              }
            } catch (e) {
              logger.warn('Could not decode token:', e);
            }
          }

          // Token doesn't have org_id, need to activate
          logger.info(`Token missing org_id, re-activating: ${orgToActivate.name}`);
          activateOrganization(orgToActivate, shouldStoreOrgId);
        });
      } else {
        // Different org needs to be activated
        activateOrganization(orgToActivate, shouldStoreOrgId);
      }
    }

    async function activateOrganization(org: Organization, storeOrgId: boolean) {
      logger.info(`Activating organization in Clerk: ${org.name} (${org.id})`);

      try {
        await setActive?.({ organization: org.id });
        logger.info('setActive() completed');

        // Wait for session to update and verify token has org_id
        let retries = 0;
        const maxRetries = 20; // Further increased

        while (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 400)); // Increased to 400ms

          // CRITICAL: Skip cache to get fresh token with org_id claim
          const token = await getToken({ skipCache: true });
          if (token) {
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));

                // Log full payload for debugging
                logger.debug(`🔍 Token check (attempt ${retries + 1}/${maxRetries}):`, {
                  has_org_id: !!payload.org_id,
                  org_id_value: payload.org_id,
                  expected: org.id,
                  match: payload.org_id === org.id,
                  org_role: payload.org_role,
                  org_permissions: payload.org_permissions,
                  all_claims: Object.keys(payload)
                });

                if (payload.org_id === org.id) {
                  // Token has the correct org_id!
                  setCurrentOrg(org);
                  if (storeOrgId) {
                    localStorage.setItem('currentOrgId', org.id);
                  }
                  logger.success(`Activated Clerk org with verified token: ${org.name} (took ${(retries + 1) * 400}ms)`);
                  setLoadingOrg(false);
                  return;
                }
              }
            } catch (e) {
              logger.warn('Error decoding token:', e);
            }
          } else {
            logger.warn(`No token returned on attempt ${retries + 1}`);
          }

          retries++;
        }

        // Timeout - check one more time what we have
        const finalToken = await getToken({ skipCache: true });
        if (finalToken) {
          try {
            const parts = finalToken.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              logger.error(`⚠️ Token verification timed out. Final token state:`, {
                has_org_id: !!payload.org_id,
                org_id: payload.org_id,
                expected: org.id,
                all_claims: Object.keys(payload)
              });
            }
          } catch {
            logger.error('Could not decode final token');
          }
        }

        logger.warn(`⚠️ Token verification timed out after ${maxRetries} attempts (${maxRetries * 400}ms), proceeding anyway`);
        setCurrentOrg(org);
        if (storeOrgId) {
          localStorage.setItem('currentOrgId', org.id);
        }
        setLoadingOrg(false);

      } catch (err) {
        logger.error('❌ Failed to activate organization in Clerk:', err);
        // Still set the org locally even if Clerk activation fails
        setCurrentOrg(org);
        setLoadingOrg(false);
      }
    }
  }, [organizations, setActive, getToken, session]);

  // Update usedSeats when members change
  useEffect(() => {
    if (currentOrg) {
      setOrganizations(prevOrgs =>
        prevOrgs.map(org =>
          org.id === currentOrg.id
            ? { ...org, usedSeats: members.length }
            : org
        )
      );
      // Update currentOrg with new usedSeats count
      setCurrentOrg(prev => {
        if (!prev || prev.id !== currentOrg.id) return prev;
        return { ...prev, usedSeats: members.length };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members.length, currentOrg?.id]); // Fixed: Added currentOrg?.id to dependencies

  // Keep the Supabase `members` mirror in sync (fire-and-forget). The frontend no
  // longer reads from it, but other backend features (run/claim attribution,
  // team analytics) still query it directly, so it needs real data to work.
  useEffect(() => {
    if (!currentOrg?.id || members.length === 0) return;

    let cancelled = false;
    const syncMembers = async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;

        await fetch(`${apiBaseUrl}/org-members/${currentOrg.id}/sync`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'x-clerk-token': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            members: members.map(m => ({
              id: m.id,
              email: m.email,
              name: m.name,
              imageUrl: m.avatar,
              role: m.role,
              joinedAt: m.joinedAt
            }))
          })
        });
      } catch (err) {
        logger.warn('Non-critical: failed to sync members to database mirror:', err);
      }
    };

    syncMembers();
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.id, members, getToken]);

  // Fetch usage data from backend
  useEffect(() => {
    const fetchUsage = async () => {
      if (!currentOrg?.id) return;

      try {
        const token = await getToken();
        if (!token) return;

        const response = await fetch(
          `${apiBaseUrl}/usage/${currentOrg.id}`,
          {
            headers: {
              'Authorization': `Bearer ${publicAnonKey}`,
              'x-clerk-token': token,
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setUsage({
            runsUsed: data.usage.runs.used,
            runsLimit: data.usage.runs.limit,
            prospectsUsed: data.usage.prospects.used,
            prospectsLimit: data.usage.prospects.limit,
            resetDate: data.periodEnd || '2025-01-01'
          });

          // Plan and seat limit come from the org's real database row now
          // (see /usage/:orgId on the backend) instead of being hardcoded to
          // 'Pilot' / 3 seats at org-load time — this is what makes an admin
          // changing a customer's plan actually show up in the product.
          if (data.plan) {
            const orgId = currentOrg.id;
            setCurrentOrg(prev =>
              prev && prev.id === orgId
                ? { ...prev, plan: data.plan, seats: data.seatsLimit ?? prev.seats }
                : prev
            );
            setOrganizations(prevOrgs =>
              prevOrgs.map(o =>
                o.id === orgId ? { ...o, plan: data.plan, seats: data.seatsLimit ?? o.seats } : o
              )
            );
          }
          logger.success('Loaded usage data');
        }
      } catch (err) {
        logger.error('Error fetching usage:', err);
      }
    };

    fetchUsage();
  }, [currentOrg?.id, getToken]);

  const switchOrganization = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (org && setActive) {
      // Set the active organization in Clerk
      setActive({ organization: orgId });
      setCurrentOrg(org);
      localStorage.setItem('currentOrgId', orgId);
    }
  };

  const inviteMember = async (email: string, role: 'admin' | 'member') => {
    if (!clerkOrganization) {
      throw new Error('No active organization');
    }

    try {
      // Clerk sends the actual invitation email — no custom backend involved.
      await clerkOrganization.inviteMember({ emailAddress: email, role: toClerkRole(role) });
      await invitations?.revalidate?.();
      logger.success(`Invited ${email} to org ${clerkOrganization.id}`);
    } catch (err) {
      logger.error('Error inviting member:', err);
      throw err;
    }
  };

  const removeMember = async (memberId: string) => {
    if (!clerkOrganization) {
      throw new Error('No active organization');
    }

    try {
      await clerkOrganization.removeMember(memberId);
      await memberships?.revalidate?.();
      logger.success(`Removed member ${memberId} from org ${clerkOrganization.id}`);
    } catch (err) {
      logger.error('Error removing member:', err);
      throw err;
    }
  };

  const updateMemberRole = async (memberId: string, role: 'admin' | 'member') => {
    if (!clerkOrganization) {
      throw new Error('No active organization');
    }

    try {
      await clerkOrganization.updateMember({ userId: memberId, role: toClerkRole(role) });
      await memberships?.revalidate?.();
      logger.success(`Updated role for member ${memberId} in org ${clerkOrganization.id} to ${role}`);
    } catch (err) {
      logger.error('Error updating member role:', err);
      throw err;
    }
  };

  const revokeInvitation = async (invitationId: string) => {
    const invitation = invitations?.data?.find(inv => inv.id === invitationId);
    if (!invitation) {
      throw new Error('Invitation not found');
    }

    try {
      await invitation.revoke();
      await invitations?.revalidate?.();
      logger.success(`Revoked invitation ${invitationId}`);
    } catch (err) {
      logger.error('Error revoking invitation:', err);
      throw err;
    }
  };

  const createOrganization = async (name: string) => {
    if (!clerkCreateOrganization) {
      throw new Error('Organization creation not available');
    }

    try {
      const newOrg = await clerkCreateOrganization({ name });
      await userMemberships?.revalidate?.();
      localStorage.setItem('currentOrgId', newOrg.id);
      await setActive?.({ organization: newOrg.id });
      logger.success(`Created organization ${newOrg.name} (${newOrg.id})`);
    } catch (err) {
      logger.error('Error creating organization:', err);
      throw err;
    }
  };

  const updateOrganizationName = async (name: string) => {
    if (!clerkOrganization) {
      throw new Error('No active organization');
    }

    try {
      await clerkOrganization.update({ name });
      await userMemberships?.revalidate?.();
      setCurrentOrg(prev => (prev ? { ...prev, name } : prev));
      setOrganizations(prev => prev.map(org => (org.id === clerkOrganization.id ? { ...org, name } : org)));
      logger.success(`Renamed organization ${clerkOrganization.id} to ${name}`);

      // Fire-and-forget: keep the Supabase mirror in sync for backend consumers.
      getToken().then(token => {
        if (!token) return;
        fetch(`${apiBaseUrl}/organizations/${clerkOrganization.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'x-clerk-token': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name })
        }).catch(err => logger.warn('Non-critical: failed to sync org name to database mirror:', err));
      });
    } catch (err) {
      logger.error('Error updating organization name:', err);
      throw err;
    }
  };

  const refreshMembers = useCallback(() => {
    memberships?.revalidate?.();
    invitations?.revalidate?.();
  }, [memberships, invitations]);

  const isCurrentUserAdmin = () => {
    // Source of truth: the current user's own membership role for the active
    // organization, straight from Clerk — not a lookup into a members list
    // that may still be loading or (previously) permanently empty.
    return currentMembership?.role === 'org:admin';
  };

  const currentUserMember = members.find(m => m.id === user?.id) || null;

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        currentOrg,
        switchOrganization,
        members,
        usage,
        inviteMember,
        removeMember,
        updateMemberRole,
        pendingInvitations,
        revokeInvitation,
        loadingMembers,
        loadingOrg,
        isCurrentUserAdmin,
        currentUserMember,
        createOrganization,
        updateOrganizationName,
        refreshMembers
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

/**
 * Organization Provider Component
 * Manages multi-tenant organization state and Clerk integration
 * Handles org switching, member management, and backend sync
 *
 * @component
 * @param props - Component props
 * @param props.children - Child components to render
 */
export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // Guard: Don't use Clerk org hooks if user is not authenticated
  if (!user) {
    return (
      <OrganizationContext.Provider
        value={{
          organizations: [],
          currentOrg: null,
          switchOrganization: () => {},
          members: [],
          usage: {
            runsUsed: 0,
            runsLimit: 100,
            prospectsUsed: 0,
            prospectsLimit: 1000,
            resetDate: '2025-01-01'
          },
          inviteMember: () => Promise.resolve(),
          removeMember: () => Promise.resolve(),
          updateMemberRole: () => Promise.resolve(),
          pendingInvitations: [],
          revokeInvitation: () => Promise.resolve(),
          loadingMembers: false,
          loadingOrg: false,
          isCurrentUserAdmin: () => false,
          currentUserMember: null,
          createOrganization: () => Promise.resolve(),
          updateOrganizationName: () => Promise.resolve(),
          refreshMembers: () => {}
        }}
      >
        {children}
      </OrganizationContext.Provider>
    );
  }

  return <OrganizationProviderImpl children={children} />;
}

/**
 * Hook to access organization context
 * Must be used within an OrganizationProvider
 * @returns OrganizationContextType with org data, members, and management functions
 * @throws Error if used outside of OrganizationProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useOrganizationContext() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider');
  }
  return context;
}
